import React, { useState } from 'react';
import {
  Button,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Visible,
  ImageCircle,
  Layout,
  useApi,
  CookieUtil,
  Form,
  Dropdown,
  Input,
  Textarea,
  CondensedLarge,
  Flashy,
  Checkbox,
  Tooltip,
  CondensedMedium,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';
import { PossibleImageSizes, TalkConst } from '@axiom/const';

import {
  isLoggedInUser,
  isClientUser as isClientUserUtil,
} from '../../utils/roles-util';
import { UserApi } from '../../api/user-api';
import { HubspotUtil } from '../../utils/hubspot-util';
import { TalentSearchFilterUtil } from '../../utils/talent-search-filter-util';
import { postAccessOurNetworkInquireWithAxiom } from '../../api/hubspot-api';
import { ContactAxiomModalSchema } from '../../schema/contact-axiom-modal-schema';
import { CandidateUtil } from '../../utils/candidate-util';

export interface ContactAxiomModalProps {
  candidate: Pick<
    Candidate,
    'id' | 'profileImageKey' | 'calculatedDisplayName'
  >;
  onClose: () => void;
}

export const ContactAxiomModal = ({
  candidate,
  onClose,
}: ContactAxiomModalProps) => {
  const isUserLoggedIn = CookieUtil.hasUserCookie() || null;
  const [{ data: user } = { data: undefined }] = useApi(
    isUserLoggedIn ? UserApi.readUser() : undefined
  );
  const [showThankYou, setShowThankYou] = useState(false);
  const [hasBlockedEmail, setHasBlockedEmail] = useState(false);
  const contact = user?.contact;

  const hasUser = !!user;
  const isClientUser = isClientUserUtil(user);
  const canSubmit = !isLoggedInUser(user) || isClientUser;
  const hasMultipleAccounts =
    isClientUser && (contact?.accounts ?? []).length > 1;
  return (
    <Form
      name="CONTACT_AXIOM_MODAL_FORM"
      schema={ContactAxiomModalSchema}
      initialValues={{
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phoneNumber: null,
        workDescription: '',
        candidateId: candidate.id,
        candidateName: candidate.calculatedDisplayName,
        candidatePhotoURL: CandidateUtil.getProfileImageUri(
          candidate,
          PossibleImageSizes.W_100
        ),
        createAccount: false,
        accountName:
          TalentSearchFilterUtil.getAccountInformation({
            accounts: user?.contact?.accounts,
          }) || undefined,
        requestType: TalkConst.requestType.Inquire,
      }}
      onSubmit={async (_, __, formData) => {
        const dataUTM = HubspotUtil.parseURLDataUtm();
        const utmArr = Object.entries(dataUTM).map(entry => ({
          name: entry[0],
          value: entry[1],
        }));

        const data = {
          fields: [
            { name: 'firstname', value: formData.firstName },
            { name: 'lastname', value: formData.lastName },
            { name: 'email', value: formData.email },
            {
              name: 'axiom_for_clients_account_display_name',
              value: formData.accountName,
            },
            {
              name: 'work_description',
              value: formData.workDescription || 'N/A',
            },
            {
              name: 'axiom_for_clients_candidate_id',
              value: formData.candidateId,
            },
            { name: 'request_type', value: formData.requestType },
            {
              name: 'i_have_read_and_agree_to_the_privacy_policy_',
              value: true,
            },
            { name: 'create_account', value: formData.createAccount },
            ...utmArr,
          ],
        };

        if (formData.phoneNumber) {
          data.fields.push({
            name: 'phone',
            value: formData.phoneNumber.replaceAll(/[ ()+-]/g, ''),
          });
        }

        const request = await postAccessOurNetworkInquireWithAxiom(data);
        if (request?.errorType === 'BLOCKED_EMAIL') {
          setHasBlockedEmail(true);
        } else {
          setShowThankYou(true);
        }
      }}
    >
      {({ fireSubmit, values }) => {
        return showThankYou ? (
          <Modal size="confirm" name="CONTACT_AXIOM_CONFIRMATION_MODAL">
            <ModalHeader
              onClose={() => {
                onClose();
              }}
              name="CONTACT_AXIOM_CONFIRMATION_MODAL_HEADER"
            >
              We're on it
            </ModalHeader>
            <ModalSection>
              We'll review your request and get back to you shortly with{' '}
              {values.firstName}'s rates and availability, typically within 1-2
              business hours.
            </ModalSection>
            <ModalFooter>
              <Button name="got_it" onClick={onClose}>
                Got it!
              </Button>
            </ModalFooter>
          </Modal>
        ) : (
          <Modal name="CONTACT_AXIOM_MODAL">
            <ModalHeader
              name="CONTACT_AXIOM_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              <Layout position="middle" horizontalGutter="16px">
                <Visible only="largeScreen">
                  <ImageCircle
                    name="candidateImage"
                    size="small"
                    imageName={values.candidateName}
                    src={values.candidatePhotoURL}
                  />
                </Visible>
                <div data-test="interested-in-candidate">
                  Interested in {values.candidateName}?
                </div>
              </Layout>
            </ModalHeader>
            <ModalSection>
              <CondensedLarge>
                Verify your contact information and we will get back to you with
                in one business day. If you need to change your name or email,
                please reach out to our{' '}
                <a href="mailto:DSS@axiomlaw.com">
                  Digital Sales Support Team.
                </a>
              </CondensedLarge>
              <Grid stackable>
                {!!hasMultipleAccounts && contact?.accounts ? (
                  <GridRow>
                    <GridColumn>
                      <Dropdown
                        name="accountName"
                        label="Account"
                        options={contact.accounts.map(account => ({
                          value: account.name,
                          label: account.calculatedName,
                        }))}
                        valueKey="value"
                        displayKey="label"
                      />
                    </GridColumn>
                  </GridRow>
                ) : null}
                <GridRow gutterVertical="16px">
                  <GridColumn>
                    <Textarea
                      description="Additional information about the type of work, any special qualifications you need, an estimated start date, and duration will help us serve you better."
                      label="Tell us more about your engagement"
                      full
                      name="workDescription"
                      placeholder="Additional information about the type of work, any
                          special qualifications you need, an estimated start
                          date, and duration will help us serve you better."
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={4}>
                    <Gutter bottom="16px">
                      <Input
                        label="First Name"
                        name="firstName"
                        disabled={hasUser}
                      />
                    </Gutter>
                  </GridColumn>
                  <GridColumn width={4}>
                    <Gutter bottom="16px">
                      <Input
                        label="Last Name"
                        name="lastName"
                        disabled={hasUser}
                      />
                    </Gutter>
                  </GridColumn>
                  <GridColumn width={4}>
                    <Gutter bottom="16px">
                      <Input label="Phone Number" name="phoneNumber" />
                    </Gutter>
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={12}>
                    <Gutter bottom="16px">
                      <Input
                        label="Business Email"
                        name="email"
                        onFocus={() => {
                          setHasBlockedEmail(false);
                        }}
                        disabled={hasUser}
                      />
                      {!!hasBlockedEmail && (
                        <Flashy
                          bold
                          color="controlAlertPrimary"
                          name="BLOCKED_EMAIL_ERROR"
                        >
                          Please enter a business email.
                        </Flashy>
                      )}
                    </Gutter>
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn>
                    {user ? null : (
                      <Gutter
                        name="createAccountTosPrivacyNoticeContainer"
                        left="16px"
                      >
                        <GridRow>
                          <GridColumn>
                            <Gutter bottom="16px">
                              <Layout position="middle">
                                <Checkbox
                                  displayValue="Create an Axiom account using my email address."
                                  name="createAccount"
                                />
                                <Visible only="largeScreen">
                                  <Tooltip position="right">
                                    Creating an Axiom Account unlocks exclusive
                                    features like talent rates and priority
                                    response to requests when you're signed in.
                                  </Tooltip>
                                </Visible>
                              </Layout>
                            </Gutter>
                          </GridColumn>
                        </GridRow>

                        <GridRow name="createAccountTosPrivacyNoticeFooter">
                          <GridColumn>
                            <CondensedMedium>
                              By clicking on{' '}
                              <Flashy bold>Agree and Submit</Flashy> you
                              acknowledge that you have read and agree to the
                              Axiom{' '}
                              <Flashy color="controlBrand">
                                <a
                                  href="https://www.axiomlaw.com/axiom-terms-of-use"
                                  target="_blank"
                                  data-test="TERMS_OF_USE"
                                  rel="noreferrer"
                                >
                                  Terms of Use
                                </a>
                              </Flashy>{' '}
                              and{' '}
                              <Flashy color="controlBrand">
                                <a
                                  href="https://www.axiomlaw.com/privacy-notice"
                                  target="_blank"
                                  data-test="PRIVACY_NOTICE"
                                  rel="noreferrer"
                                >
                                  Privacy Notice
                                </a>
                              </Flashy>
                              .<Flashy color="controlBrand">*</Flashy>
                            </CondensedMedium>
                          </GridColumn>
                        </GridRow>
                      </Gutter>
                    )}
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={async () => {
                  await fireSubmit();
                }}
                name="submit"
                disabled={!canSubmit}
              >
                Contact Axiom
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
